/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

export const EChartsLightTheme = {
  backgroundColor: 'rgba(0,0,0,0)',
  color: [
    '#5470c6',
    '#91cc75',
    '#fac858',
    '#ee6666',
    '#73c0de',
    '#3ba272',
    '#fc8452',
    '#9a60b4',
    '#ea7ccc',
  ],
  textStyle: {
    color: '#051039',
    fontSize: '10px',
    fontWeight: '400',
  },
  title: {
    textStyle: {
      color: 'rgba(5, 16, 57, 0.67)',
    },
    subtextStyle: {
      color: 'rgba(5, 16, 57, 0.67)',
    },
  },
  line: {
    itemStyle: {
      borderWidth: 1,
    },
    lineStyle: {
      width: 2,
    },
    symbolSize: 4,
    symbol: 'circle',
    smooth: false,
  },
  bar: {
    itemStyle: {
      barBorderWidth: 0,
      barBorderColor: '#ccc',
    },
  },
  scatter: {
    itemStyle: {
      borderWidth: 0,
      borderColor: '#ccc',
    },
  },
  categoryAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisLabel: {
      show: true,
      color: '#6E7079',
      fontSize: '8px',
    },
    splitLine: {
      show: false,
      lineStyle: {
        color: ['#E0E6F1'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.2)', 'rgba(210,219,238,0.2)'],
      },
    },
  },
  valueAxis: {
    axisLine: {
      show: false,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisLabel: {
      show: true,
      color: '#6E7079',
      fontSize: '8px',
    },
    splitLine: {
      show: true,
      lineStyle: {
        // Preblended over the card background so overlapping grids stay the same color.
        color: ['#e6eefd'],
        opacity: 1,
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.2)', 'rgba(210,219,238,0.2)'],
      },
    },
  },
  logAxis: {
    axisLine: {
      show: false,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisTick: {
      show: false,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisLabel: {
      show: true,
      color: '#6E7079',
      fontSize: '8px',
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: ['#E0E6F1'],
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.2)', 'rgba(210,219,238,0.2)'],
      },
    },
  },
  timeAxis: {
    nameTextStyle: {
      color: '#6E7079',
    },
    axisLine: {
      show: true,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: '#6E7079',
      },
    },
    axisLabel: {
      show: true,
      color: '#6E7079',
      fontSize: '8px',
    },
    splitLine: {
      show: false,
      lineStyle: {
        color: ['#0057ec'],
        opacity: 0.1,
      },
    },
    splitArea: {
      show: false,
      areaStyle: {
        color: ['rgba(250,250,250,0.2)', 'rgba(210,219,238,0.2)'],
      },
    },
  },
  toolbox: {
    iconStyle: {
      borderColor: '#374061',
    },
    emphasis: {
      iconStyle: {
        borderColor: '#6E7079',
      },
    },
  },
  legend: {
    textStyle: {
      color: '#333333',
    },
  },
  tooltip: {
    axisPointer: {
      lineStyle: {
        color: '#cccccc',
        width: 1,
      },
      crossStyle: {
        color: '#cccccc',
        width: 1,
      },
    },
  },
  timeline: {
    lineStyle: {
      color: '#dae1f5',
      width: 2,
    },
    itemStyle: {
      color: '#a4b1d7',
      borderWidth: 1,
    },
    controlStyle: {
      color: '#a4b1d7',
      borderColor: '#a4b1d7',
      borderWidth: 1,
    },
    checkpointStyle: {
      color: '#316bf3',
      borderColor: '#ffffff',
    },
    label: {
      color: '#a4b1d7',
    },
    emphasis: {
      itemStyle: {
        color: '#ffffff',
      },
      controlStyle: {
        color: '#a4b1d7',
        borderColor: '#a4b1d7',
        borderWidth: 1,
      },
      label: {
        color: '#a4b1d7',
      },
    },
  },
  dataZoom: {
    handleSize: '100%',
    borderColor: '#d2dbee',
    borderRadius: 3,
    textStyle: {
      color: '#051039',
    },
    dataBackground: {
      lineStyle: { color: '#d2dbee', width: 0.5 },
      areaStyle: { color: '#d2dbee', opacity: 0.2 },
    },
    selectedDataBackground: {
      lineStyle: { color: '#8fb0f7', width: 0.5 },
      areaStyle: { color: '#8fb0f7', opacity: 0.2 },
    },
    handleStyle: { color: '#fff', borderColor: '#ACB8D1' },
    moveHandleStyle: { color: '#D2DBEE', opacity: 0.7 },
    emphasis: {
      handleStyle: { borderColor: '#8FB0F7' },
      moveHandleStyle: { color: '#8FB0F7', opacity: 0.7 },
    },
  },
  markPoint: {
    label: {
      color: '#eeeeee',
    },
    emphasis: {
      label: {
        color: '#eeeeee',
      },
    },
  },
};
